import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import './FileExplorer.css';
import fileService from '../../../../services/fileService';
import ContextMenu from './ContextMenu';
import UploadModal from './UploadModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import VideoPlayerModal from './VideoPlayerModal';
import ImagePlayerModal from './ImagePlayerModal';
import axios from 'axios';

const FileExplorer = forwardRef(({ project, onFileSelect, onFolderSelect, currentPath, onBreadcrumbUpdate, selectedFolder, onClearSelection, showUploadModal, onCloseUploadModal, onFileAdded, currentFolderId }, ref) => {
  const [fileTree, setFileTree] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]); // Array of selected file objects for copy/paste/delete/download operations
  const [lastSelectedIndex, setLastSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  // Fixed column widths based on character count
  const columnWidths = {
    icon: 40,
    name: 'flex', // Name column takes remaining space
    owner: 180, // 200px fixed width
    size: 112,  // 14 characters * 8px per char (10 + 4 more)
    modified: 120, // 15 characters * 8px per char
    type: 80    // 10 characters * 8px per char
  };
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    position: { x: 0, y: 0 }
  });
  const [clipboard, setClipboard] = useState({
    files: [],
    operation: null // 'copy' or 'cut'
  });
  const [showDownloadNotification, setShowDownloadNotification] = useState(false);
  const [isClosingNotification, setIsClosingNotification] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState(''); // 'creating' or 'downloading'
  const [isMultipleFiles, setIsMultipleFiles] = useState(false);
  const [downloadCancelToken, setDownloadCancelToken] = useState(null);
  const progressIntervalRef = useRef(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [treePanelWidth, setTreePanelWidth] = useState(350);
  
  const fileListRef = useRef(null);
  const treePanelResizeRef = useRef({ startX: 0, startW: 350 });

  // Define functions before useEffects that use them
  const loadProjectFiles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📁 Loading files for project:', project.Id);
      const files = await fileService.getProjectFiles(project.Id);
      const tree = fileService.buildFileTree(files, project.Id);
      
      console.log('📁 Built file tree:', tree);
      setFileTree(tree);
      
      // Set root folder as current and auto-expand
      if (tree.length > 0) {
        setCurrentFolder(tree[0]);
        setExpandedFolders(new Set([tree[0].id]));
      }
      
    } catch (err) {
      console.error('❌ Error loading project files:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [project?.Id]);

  const findFolderById = useCallback((id) => {
    const findInTree = (items) => {
      for (const item of items) {
        if (item.id === id) return item;
        if (item.children) {
          const found = findInTree(item.children);
          if (found) return found;
        }
      }
      return null;
    };
    return findInTree(fileTree);
  }, [fileTree]);

  const buildFolderPath = useCallback((folder) => {
    if (!folder) return [{ id: 'root', name: project?.Name || 'Project Files' }];
    
    const path = [{ id: 'root', name: project?.Name || 'Project Files' }];
    
    // Build the path by traversing up the tree
    const findPath = (items, targetId, currentPath = []) => {
      for (const item of items) {
        const newPath = [...currentPath, { id: item.id, name: item.name }];
        if (item.id === targetId) {
          return newPath;
        }
        if (item.children) {
          const found = findPath(item.children, targetId, newPath);
          if (found) return found;
        }
      }
      return null;
    };
    
    const folderPath = findPath(fileTree, folder.id);
    if (folderPath) {
      return [{ id: 'root', name: project?.Name || 'Project Files' }, ...folderPath];
    }
    
    return path;
  }, [fileTree, project?.Name]);

  useEffect(() => {
    if (project && project.Id) {
      loadProjectFiles();
      
      // Push initial project URL to browser history if not already there
      const currentPath = window.location.pathname;
      const expectedPath = `/projects/${project.Id}`;
      
      if (currentPath !== expectedPath) {
        const state = { folderId: 'root' };
        window.history.pushState(state, '', expectedPath);
      }
    }
  }, [project, loadProjectFiles]);

  // Handle initial URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const folderId = urlParams.get('folder');
    
    // Also check for folder ID in the path (e.g., /projects/2746/268002)
    const pathParts = window.location.pathname.split('/');
    const pathFolderId = pathParts[pathParts.length - 1];
    
    const targetFolderId = folderId || pathFolderId;
    
    if (targetFolderId && fileTree.length > 0) {
      if (targetFolderId === 'root') {
        setCurrentFolder(fileTree[0]);
        if (onBreadcrumbUpdate) {
          onBreadcrumbUpdate([{ id: 'root', name: project?.Name || 'Project Files' }]);
        }
      } else {
        const targetFolder = findFolderById(targetFolderId);
        if (targetFolder) {
          setCurrentFolder(targetFolder);
          const folderPath = buildFolderPath(targetFolder);
          if (onBreadcrumbUpdate) {
            onBreadcrumbUpdate(folderPath);
          }
        }
      }
    }
  }, [fileTree, project, onBreadcrumbUpdate, findFolderById, buildFolderPath]);

  // Handle breadcrumb navigation
  useEffect(() => {
    if (selectedFolder === null) {
      // Navigate to root
      if (fileTree.length > 0) {
        setCurrentFolder(fileTree[0]);
      }
    } else if (selectedFolder && selectedFolder.id !== 'root') {
      // Navigate to specific folder
      const targetFolder = findFolderById(selectedFolder.id);
      if (targetFolder) {
        setCurrentFolder(targetFolder);
      }
    }
  }, [selectedFolder, fileTree, findFolderById]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = (event) => {
      // Check URL parameters first
      const urlParams = new URLSearchParams(window.location.search);
      const folderId = urlParams.get('folder');
      
      // Also check for folder ID in the path
      const pathParts = window.location.pathname.split('/');
      const pathFolderId = pathParts[pathParts.length - 1];
      
      const targetFolderId = folderId || pathFolderId;
      
      if (targetFolderId && fileTree.length > 0) {
        if (targetFolderId === 'root') {
          setCurrentFolder(fileTree.length > 0 ? fileTree[0] : null);
          if (onBreadcrumbUpdate) {
            onBreadcrumbUpdate([{ id: 'root', name: project?.Name || 'Project Files' }]);
          }
        } else {
          const targetFolder = findFolderById(targetFolderId);
          if (targetFolder) {
            setCurrentFolder(targetFolder);
            // Update breadcrumb to match the folder path
            const folderPath = buildFolderPath(targetFolder);
            if (onBreadcrumbUpdate) {
              onBreadcrumbUpdate(folderPath);
            }
          }
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [fileTree, project, onBreadcrumbUpdate, findFolderById, buildFolderPath]);

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const handleTreeFolderClick = (folder) => {
    setCurrentFolder(folder);
    const newPath = [{ id: 'root', name: project?.Name || 'Project Files' }, { id: folder.id, name: folder.name }];
    if (onBreadcrumbUpdate) {
      onBreadcrumbUpdate(newPath);
    }
    setSelectedFiles([]); // Clear selection when navigating
    if (onFolderSelect) {
      onFolderSelect(folder);
    }
    
    // Push to browser history
    const state = { folderId: folder.id };
    const url = `/projects/${project.Id}?folder=${folder.id}`;
    window.history.pushState(state, '', url);
  };

  const handleFileListDoubleClick = (item) => {
    if (item.isFolder) {
      setCurrentFolder(item);
      const newPath = [...currentPath, { id: item.id, name: item.name }];
      if (onBreadcrumbUpdate) {
        onBreadcrumbUpdate(newPath);
      }
      setSelectedFiles([]); // Clear selection when navigating
      
      // Push to browser history
      const state = { folderId: item.id };
      const url = `/projects/${project.Id}?folder=${item.id}`;
      window.history.pushState(state, '', url);
    } else {
      if (onFileSelect) {
        onFileSelect(item);
      }
    }
  };

  const handleFileListClick = (item, index, event) => {
    event.preventDefault();
    event.stopPropagation(); // Prevent event from bubbling to container
    
    // Prevent text selection during multi-selection
    if (event.ctrlKey || event.shiftKey) {
      event.preventDefault();
      window.getSelection().removeAllRanges();
    }
    
    // Handle checkbox toggle logic for the entire row
    const isSelected = selectedFiles.some(file => file.id === item.id);
    
    if (event.ctrlKey) {
      // Ctrl+Click: Toggle selection
      if (isSelected) {
        setSelectedFiles(selectedFiles.filter(file => file.id !== item.id));
      } else {
        setSelectedFiles([...selectedFiles, item]);
      }
      setLastSelectedIndex(index);
    } else if (event.shiftKey && lastSelectedIndex !== -1) {
      // Shift+Click: Range selection
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const currentFiles = getCurrentFiles();
      const rangeFiles = currentFiles.slice(start, end + 1);
      
      // Merge with existing selection, avoiding duplicates
      const existingIds = new Set(selectedFiles.map(file => file.id));
      const newFiles = rangeFiles.filter(file => !existingIds.has(file.id));
      setSelectedFiles([...selectedFiles, ...newFiles]);
    } else {
      // Regular click: Toggle selection
      if (isSelected) {
        setSelectedFiles(selectedFiles.filter(file => file.id !== item.id));
      } else {
        setSelectedFiles([item]);
      }
      setLastSelectedIndex(index);
    }
  };

  // Handle clicking on empty areas to deselect all files
  const handleFileListContentClick = (event) => {
    // Only deselect if clicking on the container itself, not on file items
    if (event.target === event.currentTarget) {
      setSelectedFiles([]);
      setLastSelectedIndex(-1);
    }
  };

  // Clear selection function that can be called from parent components
  const clearSelection = useCallback(() => {
    setSelectedFiles([]);
    setLastSelectedIndex(-1);
    // Call parent's clear selection if provided
    if (onClearSelection) {
      onClearSelection();
    }
  }, [onClearSelection]);

  // Utility functions for working with selected files
  // Note: Some utility functions are kept for potential future use
  // const getSelectedFiles = () => selectedFiles;
  // const getSelectedFileIds = () => selectedFiles.map(file => file.id);
  // const isFileSelected = (fileId) => selectedFiles.some(file => file.id === fileId);
  // const hasSelection = () => selectedFiles.length > 0;
  
  const getCurrentFiles = useCallback(() => {
    if (!currentFolder) return [];
    
    const allItems = [];
    
    // Add current folder's children (both files and folders)
    if (currentFolder.children) {
      allItems.push(...currentFolder.children);
    }
    
    // Sort items - folders first, then files, then by sort criteria
    const sortedItems = [...allItems].sort((a, b) => {
      // Folders first
      if (a.isFolder && !b.isFolder) return -1;
      if (!a.isFolder && b.isFolder) return 1;
      
      // Then sort by current sort criteria
      if (sortBy === 'name') {
        return sortOrder === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (sortBy === 'size') {
        const aSize = a.size || 0;
        const bSize = b.size || 0;
        return sortOrder === 'asc' 
          ? aSize - bSize
          : bSize - aSize;
      } else if (sortBy === 'modified') {
        const aDate = new Date(a.modified || 0);
        const bDate = new Date(b.modified || 0);
        return sortOrder === 'asc' 
          ? aDate - bDate
          : bDate - aDate;
      }
      return 0;
    });
    
    return sortedItems;
  }, [currentFolder, sortBy, sortOrder]);

  const selectAll = useCallback(() => {
    const currentFiles = getCurrentFiles();
    setSelectedFiles([...currentFiles]);
  }, [getCurrentFiles]);
  
  const getSelectedFilesCount = () => selectedFiles.length;

  // Context menu handlers
  const handleRightClick = (e, item = null) => {
    e.preventDefault();
    e.stopPropagation();
    
    // If context menu is already visible, close it first and then open at new position
    // This allows right-clicking again to move the menu (Windows Explorer behavior)
    if (contextMenu.visible) {
      // Close the old menu first
      closeContextMenu();
      // Small delay to ensure state update, then open at new position
      setTimeout(() => {
        openContextMenuAtPosition(e, item);
      }, 0);
      return;
    }
    
    // Normal flow: open menu at position
    openContextMenuAtPosition(e, item);
  };

  const openContextMenuAtPosition = (e, item = null) => {
    // If right-clicking on a specific item and it's not selected, select it
    if (item && !selectedFiles.some(file => file.id === item.id)) {
      setSelectedFiles([item]);
    }
    
    // Always use the exact cursor position from the original event
    const x = e.clientX;
    const y = e.clientY;
    
    // Debug coordinates
    console.log('Right-click coordinates:', { x, y, clientX: e.clientX, clientY: e.clientY });
    
    // Add a small offset to ensure the menu appears exactly at cursor
    const offsetX = -295;
    const offsetY = 0;
    
    // Ensure menu stays within viewport
    const menuWidth = 200;
    const menuHeight = 300; // Approximate height
    const adjustedX = (x + offsetX) + menuWidth > window.innerWidth ? (x + offsetX) - menuWidth : (x + offsetX);
    const adjustedY = (y + offsetY) + menuHeight > window.innerHeight ? (y + offsetY) - menuHeight : (y + offsetY);
    
    console.log('Adjusted coordinates:', { adjustedX, adjustedY });
    
    setContextMenu({
      visible: true,
      position: { x: adjustedX, y: adjustedY }
    });
  };

  const closeContextMenu = () => {
    setContextMenu({ visible: false, position: { x: 0, y: 0 } });
  };

  // Context menu actions
  const handleCopy = useCallback(() => {
    if (selectedFiles.length > 0) {
      setClipboard({
        files: [...selectedFiles],
        operation: 'copy'
      });
      console.log('Copied files:', selectedFiles.map(f => f.name));
    }
  }, [selectedFiles]);

  const handleCut = useCallback(() => {
    if (selectedFiles.length > 0) {
      setClipboard({
        files: [...selectedFiles],
        operation: 'cut'
      });
      console.log('Cut files:', selectedFiles.map(f => f.name));
    }
  }, [selectedFiles]);

  const handlePaste = useCallback(() => {
    if (clipboard.files.length > 0 && clipboard.operation) {
      console.log(`Pasting ${clipboard.operation} files:`, clipboard.files.map(f => f.name));
      // TODO: Implement actual paste functionality with API calls
      // This would involve calling the file service to move/copy files
    }
  }, [clipboard]);

  const handleDelete = useCallback(() => {
    if (selectedFiles.length > 0) {
      setShowDeleteModal(true);
    }
  }, [selectedFiles]);

  const confirmDelete = useCallback(async () => {
    if (selectedFiles.length === 0) return;
    
    try {
      const fileIds = selectedFiles.map(file => file.id);
      console.log('🗑️ Deleting files:', fileIds);
      
      await fileService.deleteFiles(fileIds);
      
      // Remove deleted files from the tree without full refresh
      setFileTree(prevTree => {
        const newTree = JSON.parse(JSON.stringify(prevTree)); // Deep clone
        let updatedCurrentFolder = null;
        
        const removeFromTree = (items, targetId) => {
          for (let i = items.length - 1; i >= 0; i--) {
            const item = items[i];
            if (item.id === targetId) {
              items.splice(i, 1);
              return true;
            }
            if (item.children && item.children.length > 0) {
              if (removeFromTree(item.children, targetId)) {
                // Update hasChildren if needed
                item.hasChildren = item.children.length > 0;
                // If this is the current folder, update it
                if (currentFolder && item.id === currentFolder.id) {
                  updatedCurrentFolder = item;
                }
                return true;
              }
            }
          }
          return false;
        };
        
        // Remove each deleted file from the tree
        fileIds.forEach(fileId => {
          removeFromTree(newTree, fileId);
        });
        
        // Update current folder reference if it was modified
        if (updatedCurrentFolder) {
          setCurrentFolder(updatedCurrentFolder);
        }
        
        return newTree;
      });
      
      // Clear selection
      setSelectedFiles([]);
      if (onClearSelection) {
        onClearSelection();
      }
      
      console.log('✅ Files deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting files:', error);
      alert(`Error deleting files: ${error.message}`);
    }
  }, [selectedFiles, currentFolder, onClearSelection, fileService]);

  const handleRename = useCallback(() => {
    if (selectedFiles.length === 1) {
      const newName = prompt('Enter new name:', selectedFiles[0].name);
      if (newName && newName !== selectedFiles[0].name) {
        console.log('Renaming file:', selectedFiles[0].name, 'to:', newName);
        // TODO: Implement actual rename functionality with API calls
      }
    }
  }, [selectedFiles]);

  const closeDownloadNotification = () => {
    // Trigger closing animation
    setIsClosingNotification(true);
    // Hide after animation completes (0.6s)
    setTimeout(() => {
      setShowDownloadNotification(false);
      setIsClosingNotification(false);
      setDownloadProgress(0);
      setDownloadStatus('');
      setIsMultipleFiles(false);
    }, 600);
  };

  const handleCancelDownload = () => {
    // Cancel the axios request silently
    if (downloadCancelToken) {
      downloadCancelToken.cancel('Download cancelled by user');
      setDownloadCancelToken(null);
    }
    
    // Clear progress interval if it exists
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    
    // Close with animation
    closeDownloadNotification();
    
    console.log('✅ Download cancelled by user');
  };

  const handleDownload = async () => {
    if (selectedFiles.length > 0) {
      const filesToDownload = selectedFiles.filter(file => !file.isFolder);
      const isMultiple = filesToDownload.length > 1;
      
      console.log('Downloading files:', selectedFiles.map(f => f.name));
      
      // Create cancel token for axios
      const CancelToken = axios.CancelToken;
      const source = CancelToken.source();
      setDownloadCancelToken(source);
      
      if (isMultiple) {
        // ZIP downloads - don't show old popup, use Downloads popup instead
        try {
          await fileService.downloadFiles(filesToDownload, null, source);
          setDownloadCancelToken(null);
        } catch (error) {
          // Handle cancellation silently - no alerts, just cleanup
          if (axios.isCancel(error)) {
            console.log('✅ Download cancelled by user');
            return;
          }
          
          // Only show alert for actual errors (not cancellations)
          console.error('Error downloading files:', error);
          alert(`Error downloading files: ${error.message}`);
          setDownloadCancelToken(null);
        }
      } else {
        // Single file download - show download notification
        setIsClosingNotification(false);
        setShowDownloadNotification(true);
        setIsMultipleFiles(false);
        setDownloadProgress(0);
        setDownloadStatus('downloading');
        
        try {
          // Don't pass progress callback for single file downloads
          await fileService.downloadFiles(filesToDownload, null, source);
          
          setDownloadCancelToken(null);
          
          // Keep popup visible for 2.5 seconds after completion, then hide
          setTimeout(() => {
            closeDownloadNotification();
          }, 2500);
        } catch (error) {
          // Handle cancellation silently - no alerts, just cleanup
          if (axios.isCancel(error)) {
            console.log('✅ Download cancelled by user');
            // Silently close popup - already handled by handleCancelDownload
            return; // Exit early, don't reset state again (already done in handleCancelDownload)
          }
          
          // Only show alert for actual errors (not cancellations)
          console.error('Error downloading files:', error);
          alert(`Error downloading files: ${error.message}`);
          closeDownloadNotification();
          setDownloadCancelToken(null);
        }
      }
    }
  };

  const handleRefresh = useCallback(() => {
    loadProjectFiles();
    setSelectedFiles([]);
  }, [loadProjectFiles]);

  // Add a file to the current folder without refreshing
  const addFileToCurrentFolder = useCallback((fileData) => {
    const formattedFile = fileService.formatFileForFrontend(fileData);
    
    // Update file tree
    setFileTree(prevTree => {
      const newTree = JSON.parse(JSON.stringify(prevTree)); // Deep clone
      let updatedCurrentFolder = null;
      
      const addFileToTree = (items, targetParentId) => {
        for (const item of items) {
          if (item.id === targetParentId) {
            // Found the parent folder, add file to its children
            if (!item.children) {
              item.children = [];
            }
            item.children.push({ ...formattedFile, children: [], hasChildren: false, level: item.level + 1 });
            item.hasChildren = true;
            // Sort children (folders first, then files, alphabetically)
            item.children.sort((a, b) => {
              if (a.isFolder && !b.isFolder) return -1;
              if (!a.isFolder && b.isFolder) return 1;
              return a.name.localeCompare(b.name);
            });
            // If this is the current folder, save reference for update
            if (currentFolder && item.id === currentFolder.id) {
              updatedCurrentFolder = item;
            }
            return true;
          }
          if (item.children && item.children.length > 0) {
            if (addFileToTree(item.children, targetParentId)) {
              return true;
            }
          }
        }
        return false;
      };
      
      // If parent is root (project.Id), add to root folder's children
      if (formattedFile.parentId === project.Id || formattedFile.parentId === null) {
        // Find the root folder (usually first item in tree, or a folder with parent_id === project.Id)
        const rootFolder = newTree.find(item => 
          (item.isFolder && (item.parentId === project.Id || item.parentId === null || item.id === project.Id)) ||
          (item.id === project.Id)
        ) || newTree[0]; // Fallback to first item
        
        if (rootFolder) {
          if (!rootFolder.children) {
            rootFolder.children = [];
          }
          rootFolder.children.push({ ...formattedFile, children: [], hasChildren: false, level: (rootFolder.level || 0) + 1 });
          rootFolder.hasChildren = true;
          // Sort children
          rootFolder.children.sort((a, b) => {
            if (a.isFolder && !b.isFolder) return -1;
            if (!a.isFolder && b.isFolder) return 1;
            return a.name.localeCompare(b.name);
          });
          // If viewing root, update current folder
          if (currentFolder && (currentFolder.id === rootFolder.id || currentFolder.id === 'root' || currentFolder.id === project.Id)) {
            updatedCurrentFolder = rootFolder;
          }
        } else {
          // No root folder found, add as root level item
          newTree.push({ ...formattedFile, children: [], hasChildren: false, level: 0 });
          newTree.sort((a, b) => {
            if (a.isFolder && !b.isFolder) return -1;
            if (!a.isFolder && b.isFolder) return 1;
            return a.name.localeCompare(b.name);
          });
        }
      } else {
        addFileToTree(newTree, formattedFile.parentId);
      }
      
      // Update current folder reference if it was modified
      if (updatedCurrentFolder) {
        setCurrentFolder(updatedCurrentFolder);
      }
      
      return newTree;
    });
    
    console.log('✅ File added to current folder view');
  }, [project, currentFolder, fileService]);

  // Add a folder to the current folder without refreshing
  const addFolderToCurrentFolder = useCallback((folderData) => {
    const formattedFolder = fileService.formatFileForFrontend(folderData);
    
    // Update file tree
    setFileTree(prevTree => {
      const newTree = JSON.parse(JSON.stringify(prevTree)); // Deep clone
      let updatedCurrentFolder = null;
      
      const addFolderToTree = (items, targetParentId) => {
        for (const item of items) {
          if (item.id === targetParentId) {
            // Found the parent folder, add folder to its children
            if (!item.children) {
              item.children = [];
            }
            item.children.push({ ...formattedFolder, children: [], hasChildren: false, level: item.level + 1 });
            item.hasChildren = true;
            // Sort children (folders first, then files, alphabetically)
            item.children.sort((a, b) => {
              if (a.isFolder && !b.isFolder) return -1;
              if (!a.isFolder && b.isFolder) return 1;
              return a.name.localeCompare(b.name);
            });
            // If this is the current folder, save reference for update
            if (currentFolder && item.id === currentFolder.id) {
              updatedCurrentFolder = item;
            }
            return true;
          }
          if (item.children && item.children.length > 0) {
            if (addFolderToTree(item.children, targetParentId)) {
              return true;
            }
          }
        }
        return false;
      };
      
      // If parent is root (project.Id), add to root folder's children
      if (formattedFolder.parentId === project.Id || formattedFolder.parentId === null) {
        // Find the root folder (usually first item in tree, or a folder with parent_id === project.Id)
        const rootFolder = newTree.find(item => 
          (item.isFolder && (item.parentId === project.Id || item.parentId === null || item.id === project.Id)) ||
          (item.id === project.Id)
        ) || newTree[0]; // Fallback to first item
        
        if (rootFolder) {
          if (!rootFolder.children) {
            rootFolder.children = [];
          }
          rootFolder.children.push({ ...formattedFolder, children: [], hasChildren: false, level: (rootFolder.level || 0) + 1 });
          rootFolder.hasChildren = true;
          // Sort children
          rootFolder.children.sort((a, b) => {
            if (a.isFolder && !b.isFolder) return -1;
            if (!a.isFolder && b.isFolder) return 1;
            return a.name.localeCompare(b.name);
          });
          // If viewing root, update current folder
          if (currentFolder && (currentFolder.id === rootFolder.id || currentFolder.id === 'root' || currentFolder.id === project.Id)) {
            updatedCurrentFolder = rootFolder;
          }
        } else {
          // No root folder found, add as root level item
          newTree.push({ ...formattedFolder, children: [], hasChildren: false, level: 0 });
          newTree.sort((a, b) => {
            if (a.isFolder && !b.isFolder) return -1;
            if (!a.isFolder && b.isFolder) return 1;
            return a.name.localeCompare(b.name);
          });
        }
      } else {
        addFolderToTree(newTree, formattedFolder.parentId);
      }
      
      // Update current folder reference if it was modified
      if (updatedCurrentFolder) {
        setCurrentFolder(updatedCurrentFolder);
      }
      
      return newTree;
    });
    
    console.log('✅ Folder added to current folder view');
  }, [project, currentFolder, fileService]);

  // Expose handleDownload, handleRefresh, addFileToCurrentFolder, addFolderToCurrentFolder, and selectedFiles to parent component via ref
  useImperativeHandle(ref, () => ({
    handleDownload: () => {
      if (selectedFiles.length > 0) {
        handleDownload();
      } else {
        alert('Please select files to download');
      }
    },
    handleRefresh: () => {
      handleRefresh();
    },
    addFileToCurrentFolder: (fileData) => {
      addFileToCurrentFolder(fileData);
    },
    addFolderToCurrentFolder: (folderData) => {
      addFolderToCurrentFolder(folderData);
    },
    hasSelectedFiles: selectedFiles.length > 0
  }), [selectedFiles, handleDownload, handleRefresh, addFileToCurrentFolder, addFolderToCurrentFolder]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (contextMenu.visible) return; // Don't handle shortcuts when context menu is open
      
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'c':
            e.preventDefault();
            handleCopy();
            break;
          case 'x':
            e.preventDefault();
            handleCut();
            break;
          case 'v':
            e.preventDefault();
            handlePaste();
            break;
          case 'a':
            e.preventDefault();
            selectAll();
            break;
          default:
            // No action for other key combinations
            break;
        }
      } else {
        switch (e.key) {
          case 'Escape':
            e.preventDefault();
            clearSelection();
            break;
          case 'Delete':
            e.preventDefault();
            handleDelete();
            break;
          case 'F2':
            e.preventDefault();
            handleRename();
            break;
          case 'F5':
            e.preventDefault();
            handleRefresh();
            break;
          default:
            // No action for other keys
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedFiles, contextMenu.visible, clipboard, clearSelection, handleCopy, handleCut, handleDelete, handlePaste, handleRefresh, handleRename, selectAll]);


  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };


  const renderTreeItem = (item, level = 0) => {
    // Only render folders in the tree view
    if (!item.isFolder) {
      return null;
    }
    
    const isExpanded = expandedFolders.has(item.id);
    const isSelected = currentFolder && currentFolder.id === item.id;
    const hasChildren = item.children && item.children.filter(child => child.isFolder).length > 0;
    
    // Use open folder icon when folder is selected, otherwise use regular folder icon
    let icon;
    if (item.isFolder) {
      icon = isSelected ? 'fas fa-folder-open' : 'fas fa-folder';
    } else {
      icon = fileService.getFileIcon(item);
    }
    
    return (
      <div key={item.id} className="file-tree-item-wrapper">
        <div 
          className={`file-tree-item ${isSelected ? 'selected' : ''}`}
          style={{ paddingLeft: `${level * 18 + 2}px` }}
          onClick={(e) => {
            e.stopPropagation();
            handleTreeFolderClick(item);
          }}
        >
          <div className="file-tree-item-content">
            <button 
              className="expand-btn"
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(item.id);
              }}
            >
              <i className={`fas fa-chevron-right ${isExpanded ? 'expanded' : ''}`}></i>
            </button>
            
            <div className="file-icon">
              <i className={icon}></i>
            </div>
            
            <span className="file-name">{item.name}</span>
          </div>
        </div>
        
        {isExpanded && hasChildren && (
          <div className="tree-children">
            {item.children
              .filter(child => child.isFolder)
              .map(child => renderTreeItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderFileListHeader = () => (
    <div className="file-list-header">
      <div 
        className="header-cell icon" 
        style={{ width: `${columnWidths.icon}px` }}
      >
        <i className="fas fa-image"></i>
      </div>
      
      <div 
        className="header-cell name"
        style={{ flex: 1 }}
        onClick={() => handleSort('name')}
      >
        <span>Name</span>
        {sortBy === 'name' && (
          <i className={`fas fa-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
        )}
      </div>
      
      <div 
        className="header-cell owner"
        style={{ width: `${columnWidths.owner}px` }}
        onClick={() => handleSort('owner')}
      >
        <span>Owner</span>
        {sortBy === 'owner' && (
          <i className={`fas fa-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
        )}
      </div>
      
      <div 
        className="header-cell size"
        style={{ width: `${columnWidths.size}px` }}
        onClick={() => handleSort('size')}
      >
        <span>Size</span>
        {sortBy === 'size' && (
          <i className={`fas fa-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
        )}
      </div>
      
      <div 
        className="header-cell modified"
        style={{ width: `${columnWidths.modified}px` }}
        onClick={() => handleSort('modified')}
      >
        <span>Modified</span>
        {sortBy === 'modified' && (
          <i className={`fas fa-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
        )}
      </div>
      
      <div 
        className="header-cell type"
        style={{ width: `${columnWidths.type}px` }}
        onClick={() => handleSort('type')}
      >
        <span>Type</span>
        {sortBy === 'type' && (
          <i className={`fas fa-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
        )}
      </div>
    </div>
  );

  const handlePlayVideo = (e, file) => {
    e.stopPropagation(); // Prevent file selection when clicking play icon
    setSelectedVideoFile(file);
    setShowVideoModal(true);
  };

  const handleViewImage = (e, file) => {
    e.stopPropagation(); // Prevent file selection when clicking view icon
    setSelectedImageFile(file);
    setShowImageModal(true);
  };

  const renderFileListItem = (item, index) => {
    const isSelected = selectedFiles.some(file => file.id === item.id);
    const icon = fileService.getFileIcon(item);
    const fileExtension = item.isFolder ? 'Folder' : (item.name.split('.').pop()?.toLowerCase() || '');
    const isVideoFile = !item.isFolder && fileExtension === 'mp4';
    // Common image extensions
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'ico', 'tiff', 'tif'];
    const isImageFile = !item.isFolder && imageExtensions.includes(fileExtension);
    
    return (
      <div 
        key={item.id}
        className={`file-list-item ${isSelected ? 'selected' : ''} ${item.isFolder ? 'folder-item' : 'is-file'}`}
        onClick={(e) => handleFileListClick(item, index, e)}
        onDoubleClick={() => handleFileListDoubleClick(item)}
        onContextMenu={(e) => {
          // Ensure file item right-click handler fires even when backdrop is visible
          // Stop propagation early so backdrop doesn't capture it
          e.stopPropagation();
          handleRightClick(e, item);
        }}
      >
        <div 
          className="file-icon"
          style={{ width: `${columnWidths.icon}px` }}
        >
          <i className={icon}></i>
        </div>
        <div 
          className="file-name"
          style={{ flex: 1 }}
        >
          <span className="file-name-text">{item.name}</span>
          {isVideoFile && (
            <button
              className="video-play-button"
              onClick={(e) => handlePlayVideo(e, item)}
              title="Play video"
              aria-label="Play video"
            >
              <span className="video-play-icon-circle">
                <i className="fas fa-play"></i>
              </span>
              <span className="video-play-text">Play</span>
            </button>
          )}
          {isImageFile && (
            <button
              className="image-view-button"
              onClick={(e) => handleViewImage(e, item)}
              title="View image"
              aria-label="View image"
            >
              <span className="image-view-icon-circle">
                <i className="fas fa-image"></i>
              </span>
              <span className="image-view-text">View</span>
            </button>
          )}
        </div>
        <div 
          className="file-owner"
          style={{ width: `${columnWidths.owner}px` }}
        >
          {item.ownerName || 'Unknown'}
        </div>
        <div 
          className="file-size"
          style={{ width: `${columnWidths.size}px` }}
        >
          {item.isFolder ? '--' : fileService.formatFileSize(item.size)}
        </div>
        <div 
          className="file-modified"
          style={{ width: `${columnWidths.modified}px` }}
        >
          {item.modified ? new Date(item.modified).toLocaleDateString() : '--'}
        </div>
        <div 
          className="file-type"
          style={{ width: `${columnWidths.type}px` }}
        >
          {item.isFolder ? 'Folder' : fileExtension.toUpperCase()}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="file-explorer-loading">
        <div className="file-explorer-loading-spinner"></div>
        <span>Loading files...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="file-explorer-error">
        <i className="fas fa-exclamation-triangle"></i>
        <span>Error loading files: {error}</span>
        <button onClick={loadProjectFiles} className="retry-btn">
          <i className="fas fa-refresh"></i>
          Retry
        </button>
      </div>
    );
  }

  const currentFiles = getCurrentFiles();

  const handleTreePanelResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    treePanelResizeRef.current = { startX: e.clientX, startW: treePanelWidth };
    const onMove = (e2) => {
      const dx = e2.clientX - treePanelResizeRef.current.startX;
      const newW = treePanelResizeRef.current.startW + dx;
      setTreePanelWidth(Math.min(500, Math.max(300, newW)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <div className="file-explorer">
      <div className="file-explorer-content">
        {/* Tree Panel - resizable via right edge (300–500px, default 350px) */}
        <div 
          className="tree-panel"
          style={{ width: treePanelWidth }}
          onClick={clearSelection}
        >
          <div className="file-tree-content">
            {fileTree
              .filter(item => item.isFolder)
              .map(item => renderTreeItem(item))}
          </div>
          {showDownloadNotification && (
            <div className={`download-notification ${isClosingNotification ? 'closing' : ''}`}>
              <div className="download-notification-content">
                <div className="download-notification-header">
                  {isMultipleFiles ? (
                    <i className={`fas ${downloadStatus === 'creating' ? 'fa-file-archive' : 'fa-download'}`}></i>
                  ) : (
                    <div className="download-loading-circle"></div>
                  )}
                  <span>
                    {isMultipleFiles && downloadStatus === 'creating' 
                      ? 'Creating ZIP...' 
                      : 'Downloading files...'}
                  </span>
                </div>
                {isMultipleFiles && (
                  <div className="download-progress-container">
                    <div className="download-progress-bar">
                      <div 
                        className="download-progress-fill" 
                        style={{ width: `${downloadProgress}%` }}
                      ></div>
                    </div>
                    <span className="download-progress-text">{Math.round(downloadProgress)}%</span>
                    {downloadStatus === 'creating' && (
                      <button 
                        className="download-cancel-btn"
                        onClick={handleCancelDownload}
                        title="Cancel ZIP"
                      >
                        <i className="fas fa-times"></i>
                        <span className="cancel-tooltip">Cancel ZIP</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <div
            className="tree-panel-resize-handle"
            onMouseDown={handleTreePanelResizeStart}
            title="Drag to resize"
            aria-label="Resize tree panel"
          />
        </div>

        {/* File List Panel */}
        <div className="file-list-panel">
          {renderFileListHeader()}
          <div 
            className="file-list-content" 
            ref={fileListRef}
            onContextMenu={(e) => handleRightClick(e)}
            onClick={handleFileListContentClick}
          >
            {currentFiles.length === 0 ? (
              <div className="empty-folder">
                <i className="fas fa-folder-open"></i>
                <span>This folder is empty</span>
              </div>
            ) : (
              <>
                {currentFiles.map((item, index) => renderFileListItem(item, index))}
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="file-explorer-footer">
        <div className="footer-content">
          <span className="total-files">
            {getCurrentFiles().length} item{getCurrentFiles().length !== 1 ? 's' : ''}
          </span>
          {selectedFiles.length > 0 && (
            <>
              <span className="footer-separator">|</span>
              <span className="selected-count">
                {getSelectedFilesCount()} selected
              </span>
            </>
          )}
        </div>
      </div>
      
      {/* Context Menu */}
      <ContextMenu
        visible={contextMenu.visible}
        position={contextMenu.position}
        onClose={closeContextMenu}
        onRightClickOnBackdrop={(e) => {
          // When right-clicking on backdrop while menu is visible:
          // Close old menu and open new one at cursor position
          // The menu will open with currently selected files (or empty if clicking on empty space)
          closeContextMenu();
          
          // Use setTimeout to ensure state update completes before opening new menu
          setTimeout(() => {
            // Open menu at new position
            // If clicking on a file item, that item's handler would have fired first (before backdrop)
            // Since backdrop fires, we know it's empty space, so pass null for item
            openContextMenuAtPosition(e, null);
          }, 0);
        }}
        selectedFiles={selectedFiles}
        onCopy={handleCopy}
        onPaste={handlePaste}
        onDelete={handleDelete}
        onRename={handleRename}
        onDownload={handleDownload}
        onCut={handleCut}
        onSelectAll={selectAll}
        onRefresh={handleRefresh}
        canPaste={clipboard.files.length > 0}
        canRename={selectedFiles.length === 1}
        canDelete={selectedFiles.length > 0}
        canDownload={selectedFiles.length > 0}
      />
      
      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={onCloseUploadModal}
        onUpload={loadProjectFiles}
        onFileAdded={onFileAdded}
        projectId={project?.Id}
        currentFolderId={currentFolderId || currentFolder?.id || project?.Id}
        projectName={project?.Name}
        currentFolderName={currentFolder && currentFolder.id !== project?.Id ? currentFolder.name : null}
      />
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        items={selectedFiles}
      />
      
      {/* Video Player Modal */}
      <VideoPlayerModal
        isOpen={showVideoModal}
        onClose={() => {
          setShowVideoModal(false);
          setSelectedVideoFile(null);
        }}
        file={selectedVideoFile}
      />
      
      {/* Image Player Modal */}
      <ImagePlayerModal
        isOpen={showImageModal}
        onClose={() => {
          setShowImageModal(false);
          setSelectedImageFile(null);
        }}
        file={selectedImageFile}
      />
    </div>
  );
});

FileExplorer.displayName = 'FileExplorer';

export default FileExplorer;
